import { call, put, select } from 'redux-saga/effects';
import * as actions from './actions';
import { produce } from 'immer';
import axios from 'axios';
import { sagas as eventSagas } from 'store/event';
import { updateTotalCount } from 'store/helpers/tableAndToolbar/actions';
import { getParams } from 'store/helpers/helperFunctions';
import { convertToDateTimeFormat2OnlyString } from 'helpers/timeUtils';
import moment from 'moment';
import { excludeProductNames } from 'constants.js';
import { updateEventReportFilters } from './actions';

const { waitForGetSupportedEventTypes } = eventSagas;

const getTransformedEventReport = eventReport => {
  eventReport.createdOn = convertToDateTimeFormat2OnlyString(eventReport.createTime);
  eventReport.updatedOn = convertToDateTimeFormat2OnlyString(eventReport.updateTime);

  return eventReport;
};
const getTransformedEventHistory = (history, campaignDetailsMap, templateDetailsMap) => {
    const currentDate = new Date();
    const eventDate = new Date(history.eventTime);
    const diffTime = Math.abs(currentDate - eventDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
    history.eventTime = convertToDateTimeFormat2OnlyString(history.eventTime);
    history.campaignName = campaignDetailsMap[history.campaignUuid]?.name;
    history.templateName = templateDetailsMap[history.communicationTemplateUuid]?.name;
    if (diffDays > 90) {
        history.isExpired = true;
    } else {
        history.isExpired = false;
    }
  return history;
};

const getTransformedEventLog = (log) => {
  log.occurredTimeAndDate = convertToDateTimeFormat2OnlyString(log.occurred);
  log.payload = log.inputEvent ? JSON.parse(log.inputEvent) : {};
  return log;
};

// the actual http calls
const apiRoot = `${window._env_?.REACT_APP_API_PATH}`;
const schemaApiRoot = `${window._env_?.REACT_APP_SCHEMA_API_PATH}`;

const getEventReportsApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}`;

const getCampaignApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/campaign`;

const getTemplateApiRoot = (tenantId, businessContextId) =>
  `${apiRoot}tenants/${tenantId}/context/${businessContextId}/comm-template`;

const getSchemaApiRoot = (tenantId, businessContextId) =>
    `${schemaApiRoot}tenants/${tenantId}/context/${businessContextId}`;

export const getPaginatedEventsAsync = (
  tenantId,
  businessContextId,
  urlParams,
  bodyParams
) => {
  return axios
    .post(
      `${getEventReportsApiRoot(tenantId, businessContextId)}/event-report/search?index=${
        urlParams.index
      }&pageSize=${urlParams.pageSize}`,
      bodyParams
    )
    .then(response => ({
      data: response.data,
      totalRecords: parseInt(response.headers['x-ag-totalcount'])
    }))
    .catch(err => {
      throw err;
    });
};

export const getCampaignDetails = (
    tenantId,
    businessContextId,
    campaignUuids
  ) => {
  return axios
    .post(`${getCampaignApiRoot(tenantId, businessContextId)}/uuid/bulk`,
      campaignUuids
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export const getTemplateDetails = (
    tenantId,
    businessContextId,
    communicationTemplateUuids
  ) => {
  return axios
    .post(`${getTemplateApiRoot(tenantId, businessContextId)}/uuid/bulk`,
      communicationTemplateUuids
    )
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

export const getEventReportsCsvFileAsync = (
  tenantId, 
  businessContextId,
  payload
) =>
  axios({
    url: `${getEventReportsApiRoot(tenantId, businessContextId)}/event-report/search/export`,
    method: 'POST',
    responseType: 'blob',
    data: payload
  })
  .then(response => response.data)
    .catch(err => {
      throw err;
    });

export const getEventLogAsync = (
    tenantId,
    businessContextId,
    eventId,
    partialCollectionName
  ) => {
  return axios
    .get(`${getSchemaApiRoot(tenantId, businessContextId)}/event/collection/${partialCollectionName}/log/${eventId}`)
    .then(response => response.data)
    .catch(err => {
      throw err;
    });
};

// selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;
const currentBusinessContextId = store =>
  store.user.selectedBusinessContext.businessContextId;
const paginatedEventReportList = store =>
  store.eventReports ? store.eventReports.paginatedList : [];
const pageNo = store =>
  store.eventReports ? store.eventReports.filters?.pageNo : 0;
const searchInput = store =>
  store.eventReports.filters ? store.eventReports.filters?.searchInput : '';
const sortBy = store =>
  store.eventReports.filters ? store.eventReports.filters?.sortBy : 'updateTime';
const sortOrder = store =>
  store.eventReports.filters ? store.eventReports.filters?.sortOrder : 'ASC';
const rowsPerPage = store =>
  store.eventReports.filters ? store.eventReports.filters?.rowsPerPage : 10;
const templates = store =>
  store.eventReports.filters ? store.eventReports.filters?.filterPaginatedTemplates : [];
const campaigns = store =>
  store.eventReports.filters ? store.eventReports.filters?.filterPaginatedCampaigns : [];
const filterProductNames = store =>
  store.eventReports.filters ? store.eventReports.filters?.filterProductNames : [];
const filterEventTypeCategories = store =>
  store.eventReports.filters ? store.eventReports.filters?.filterEventTypeCategories : [];
const productNames = store => store.events.supportedEventTypes.productNames;
const productCategories = store => store.events.supportedEventTypes.productCategories;
const filterStartDate = store =>
  store.eventReports.filters ? store.eventReports.filters?.filterStartDate : null;
const filterEndDate = store =>
  store.eventReports.filters ? store.eventReports.filters?.filterEndDate : null;
const filterEventIdentifierValues = store =>
  store.eventReports.filters ? store.eventReports.filters?.filterEventIdentifierValues : {};
const searchScopes = store =>
  store.eventReports.filters ? store.eventReports.filters?.searchScopes : null;
const tableAndToolbarRowsPerPage = store =>
  store.tableAndToolbar ? store.tableAndToolbar.rowsPerPage : 10;
const tableAndToolbarPageNo = store =>
  store.tableAndToolbar ? store.tableAndToolbar.pageNo : 0;

const getLocalParams = requestParams => {
  for (let param in requestParams) {
    if (param == 'emailAddress' && requestParams[param].length == 0) {
      delete requestParams[param];
    }
    if (param === 'sortBy' && requestParams[param] === 'createdOn') {
      requestParams[param] = 'createTime';
    }
    if (param === 'communicationTemplateUuids' || param === 'campaignUuids') {
      requestParams[param] = requestParams[param].map(item => {
        return item.value;
      });
    }
    if (param === 'fromDate' && !(requestParams[param] && moment(requestParams[param]).startOf('day').isSameOrBefore(moment().startOf('day')))) {
      delete requestParams[param];
    }
    if (param === 'toDate' && !(requestParams[param] && moment(requestParams[param]).startOf('day').isSameOrBefore(moment().startOf('day'))
      && (requestParams['fromDate'] ? moment(requestParams[param]).startOf('day').isSameOrAfter(moment(requestParams['fromDate']).startOf('day')) : true))) {
      delete requestParams[param];
    }
    if (param === 'eventIdentifierValues') {
      let eventIdentifierValues = requestParams[param].reduce((acc, item) => {
        acc[item.value] = item.regex.trim();
        return acc;
      }, {});
      for (let key in eventIdentifierValues) {
        if (!eventIdentifierValues[key]) {
          delete eventIdentifierValues[key];
        }
      }
      if (eventIdentifierValues && Object.keys(eventIdentifierValues).length > 0) {
        requestParams[param] = eventIdentifierValues;
      } else {
        delete requestParams[param];
      }
    }
  }
  return requestParams;
};
export function* getPaginatedEventReportsIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let currentList = yield select(paginatedEventReportList);
    yield call(waitForGetSupportedEventTypes);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);
    let excludeProductName = productName.filter(name => !excludeProductNames.includes(name))
    if (
      action.type === actions.GET_PAGINATED_EVENT_REPORTS &&
      excludeProductName.length > 0 &&
      (currentList.length === 0 || payload.force === true)
    ) {
      // Use searchScopes only when explicitly provided in payload or stored in filters (toggle ON)
      const searchScopesValue = ('searchScopes' in payload)
        ? payload.searchScopes
        : (yield select(searchScopes));

      const requestBody = {
        emailAddress: yield select(searchInput),
        sortBy: yield select(sortBy),
        sortOrder: yield select(sortOrder),
        communicationTemplateUuids: yield select(templates),
        campaignUuids: yield select(campaigns),
        productNames: yield select(filterProductNames),
        eventTypeCategories: yield select(filterEventTypeCategories),
        fromDate: yield select(filterStartDate),
        toDate: yield select(filterEndDate),
        eventIdentifierValues: yield select(filterEventIdentifierValues),
        ...(searchScopesValue && { searchScopes: searchScopesValue })
      };
      let urlParams = {
        index: yield select(pageNo),
        pageSize: yield select(rowsPerPage)
      };
      let bodyParams = { ...getParams(getLocalParams(requestBody), productName, productCategory) };
      bodyParams = {
        ...bodyParams,
        productNames: bodyParams.productNames.filter(name => !excludeProductNames.includes(name))
      }
      yield put(actions.gettingPaginatedEventReports());
      currentList = yield call(
        getPaginatedEventsAsync,
        tenantId,
        businessContextId,
        urlParams,
        bodyParams
      );
      currentList.data = produce(currentList.data, eventReports => {
        eventReports = eventReports.map(getTransformedEventReport);
      });
      yield put(updateEventReportFilters({ totalCount: currentList.totalRecords }));
    }
    yield put(actions.gettingPaginatedEventReportsSucceeded(currentList.data || []));
  } catch (ex) {
    yield put(updateEventReportFilters({ totalCount: 0 }));
    yield put(actions.gettingPaginatedEventReportsFailed(ex));
  }
}

export function* getPaginatedEventHistoryIfNeeded(action) {
  const { payload = [] } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let pageSize = yield select(tableAndToolbarRowsPerPage);
    let index = yield select(tableAndToolbarPageNo);
    let currentEventHistory = [];
    if (
      action.type === actions.GET_PAGINATED_EVENT_HISTORY
    ) {
      const paginate = (array, page_size, page_number) => {
        return array.slice(page_number * page_size, (page_number + 1) * page_size);
      };
      const getDetailsMap = function* (items, key, getDetailsFunc) {
        let uuids = items?.map(item => item[key]);
        let details = uuids ? yield call(getDetailsFunc, tenantId, businessContextId, uuids) : [];
        let detailsMap = {};
        details.forEach(detail => {
          detailsMap[detail.uuid] = detail;
        });
        return detailsMap;
      };
      if (payload?.eventHistory?.length) {
        let campaignDetailsMap = yield* getDetailsMap(payload.eventHistory, 'campaignUuid', getCampaignDetails);
        let templateDetailsMap = yield* getDetailsMap(payload.eventHistory, 'communicationTemplateUuid', getTemplateDetails);
        currentEventHistory = paginate(payload.eventHistory, pageSize, index);      
        currentEventHistory = produce(currentEventHistory, eventHistory => {
          eventHistory = eventHistory.map((history) => getTransformedEventHistory(history, campaignDetailsMap, templateDetailsMap));
        });
        yield put(updateTotalCount(payload?.eventHistory.length));
      } else {
        yield put(updateTotalCount(0));
      }
      yield put(actions.gettingPaginatedEventHistorySucceeded(currentEventHistory));
    }
  } catch (ex) {
    yield put(updateTotalCount(0));
    yield put(actions.gettingPaginatedEventReportsFailed(ex));
  }
}

export function* getEventReportCsvFile(action) {
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    let productCategory = yield select(productCategories);
    let productName = yield select(productNames);

    // Read searchScopes from filters state to ensure CSV matches displayed data
    const searchScopesValue = yield select(searchScopes);

    const requestBody = {
      emailAddress: yield select(searchInput),
      sortBy: yield select(sortBy),
      sortOrder: yield select(sortOrder),
      communicationTemplateUuids: yield select(templates),
      campaignUuids: yield select(campaigns),
      productNames: yield select(filterProductNames),
      eventTypeCategories: yield select(filterEventTypeCategories),
      fromDate: yield select(filterStartDate),
      toDate: yield select(filterEndDate),
      eventIdentifierValues: yield select(filterEventIdentifierValues),
      ...(searchScopesValue && { searchScopes: searchScopesValue })
    };
    let bodyParams = { ...getParams(getLocalParams(requestBody), productName, productCategory) };

    let response = yield call(getEventReportsCsvFileAsync, tenantId, businessContextId, bodyParams);
    yield put(actions.gettingEventReportCsvFileSucceeded(response));
  } catch (ex) {
    yield put(actions.gettingEventReportCsvFileFailed(ex));
  }
}

export function* getEventLogIfNeeded(action) {
  const { payload = {} } = action;
  try {
    let tenantId = yield select(currentTenantId);
    let businessContextId = yield select(currentBusinessContextId);
    if (payload.eventId) {
      let response = yield call(getEventLogAsync, tenantId, businessContextId, payload.eventId, payload.partialCollectionName);
      response = produce(response, log => {
        log = log.map(getTransformedEventLog);
      });
      yield put(actions.gettingEventLogSucceeded(response));
    } else {
      yield put(actions.gettingEventLogFailed('Event ID is not available'));
    }
  } catch (ex) {
    yield put(actions.gettingEventLogFailed(ex));
  }
}